"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.diffHotelLineDescriptions = void 0;
const getHotelLineDescriptions_1 = require("./getHotelLineDescriptions");
/**
 * Build a stable hash key for a grouped room line.
 * Tune this to match your actual grouping criteria.
 */
const makeGroupKey = (d) => {
    const r = d.room ?? {};
    // Safely pick fields usually used for grouping
    const name = (r.name ?? "").toString().trim();
    const board = (r.board ?? r.board_code ?? "").toString().trim();
    const bedCfg = (r.bed_configuration ?? "").toString().trim();
    const view = (r.view ?? "").toString().trim();
    const size = (r.room_size ?? "").toString().trim();
    // Extra beds & nominal occupancy typically affect title and grouping in your code
    const extraBeds = Number(r.extra_beds ?? 0);
    const occ = Number(r.occupancy ?? d.occupancy_raw ?? 0);
    // If your grouping ignores some of these, remove them here
    return [
        name,
        `board:${board}`,
        bedCfg && `bed:${bedCfg}`,
        view && `view:${view}`,
        size && `size:${size}`,
        `occ:${occ}`,
        `xbed:${extraBeds}`,
    ]
        .filter(Boolean)
        .join("|");
};
const diffHotelLineDescriptions = (previous, current) => {
    const prevList = ((0, getHotelLineDescriptions_1.getHotelLineDescriptions)(previous) ||
        []);
    const currList = ((0, getHotelLineDescriptions_1.getHotelLineDescriptions)(current) ||
        []);
    const prevMap = new Map();
    const currMap = new Map();
    prevList.forEach((d) => prevMap.set(makeGroupKey(d), d));
    currList.forEach((d) => currMap.set(makeGroupKey(d), d));
    const added = [];
    const removed = [];
    const modified = [];
    // Added / modified
    for (const [k, c] of currMap) {
        const p = prevMap.get(k);
        if (!p) {
            added.push(c);
        }
        else {
            const deltas = {};
            if (p.quantity !== c.quantity) {
                deltas.quantity = {
                    from: p.quantity,
                    to: c.quantity,
                    diff: c.quantity - p.quantity,
                };
            }
            if (p.occupancy_raw !== c.occupancy_raw) {
                deltas.occupancy_raw = {
                    from: p.occupancy_raw,
                    to: c.occupancy_raw,
                    diff: c.occupancy_raw - p.occupancy_raw,
                };
            }
            if (p.title !== c.title) {
                deltas.titleChanged = { from: p.title, to: c.title };
            }
            if ((p.extras || "") !== (c.extras || "")) {
                deltas.extrasChanged = { from: p.extras || "", to: c.extras || "" };
            }
            if (Object.keys(deltas).length > 0)
                modified.push({ key: k, prev: p, curr: c, deltas });
        }
    }
    // Removed
    for (const [k, p] of prevMap) {
        if (!currMap.has(k))
            removed.push(p);
    }
    const hasChanged = added.length > 0 || removed.length > 0 || modified.length > 0;
    // Friendly bullets using your titles
    const bullets = [
        ...added.map((d) => `+ ${d.title}`),
        ...removed.map((d) => `− ${d.title}`),
        ...modified.flatMap(({ prev, curr, deltas }) => {
            const lines = [];
            if (deltas.quantity) {
                lines.push(`↺ ${curr.title}: quantity ${deltas.quantity.from} → ${deltas.quantity.to}`);
            }
            if (deltas.occupancy_raw) {
                lines.push(`↺ ${curr.title}: occupancy ${deltas.occupancy_raw.from} → ${deltas.occupancy_raw.to}`);
            }
            if (deltas.titleChanged && !deltas.quantity && !deltas.occupancy_raw) {
                // If only the wording changed (e.g., extra bed text), show a rename-style note
                lines.push(`↺ ${deltas.titleChanged.from} → ${deltas.titleChanged.to}`);
            }
            if (deltas.extrasChanged) {
                lines.push(`↺ ${curr.title}: extras changed (${deltas.extrasChanged.from || "—"} → ${deltas.extrasChanged.to || "—"})`);
            }
            return lines;
        }),
    ];
    return {
        hasChanged,
        added,
        removed,
        modified,
        bullets,
        // Some quick counts if you want a summary
        summary: {
            previous_count: prevList.reduce((s, d) => s + (d.quantity || 0), 0),
            current_count: currList.reduce((s, d) => s + (d.quantity || 0), 0),
            added: added.length,
            removed: removed.length,
            modified: modified.length,
        },
    };
};
exports.diffHotelLineDescriptions = diffHotelLineDescriptions;
